import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Heart, Truck, ShieldCheck, RotateCcw, Star } from "lucide-react";
import { toast } from "sonner";

import Layout from "@/components/Layout";
import api, { formatINR } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { ScrollFade } from "@/lib/animations";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [pincode, setPincode] = useState("");
  const [delivery, setDelivery] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setProduct(null); setActive(0); setSize(""); setColor(""); setQty(1); setDelivery(null);
    api.get(`/products/${id}`).then(r => {
      setProduct(r.data);
      setSize(r.data.sizes?.[0] || "");
      setColor(r.data.colors?.[0] || "");
      api.get(`/products?category=${r.data.category}&limit=8`).then(rr => {
        setRelated((rr.data || []).filter(p => p.id !== r.data.id).slice(0, 4));
      });
    }).catch(() => {});
  }, [id]);

  if (!product) {
    return <Layout><div className="min-h-[60vh] flex items-center justify-center text-[#163A2F]/60 overline">Loading…</div></Layout>;
  }

  const discount = product.original_price && product.original_price > product.price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : null;

  function handleAdd() {
    if (!size) { toast.error("Please select a size."); return; }
    addItem({
      product_id: product.id,
      name: product.name,
      image: product.images?.[0],
      price: product.price,
      size, color, quantity: qty,
    });
    toast.success("Added to bag");
  }

  function handleBuyNow() {
    if (!size) { toast.error("Please select a size."); return; }
    addItem({ product_id: product.id, name: product.name, image: product.images?.[0], price: product.price, size, color, quantity: qty });
    navigate("/checkout");
  }

  async function checkDelivery() {
    if (!/^\d{6}$/.test(pincode)) { toast.error("Enter a valid 6-digit pincode"); return; }
    try {
      const { data } = await api.post("/shipping/rates", { pincode, weight: 1.0, cod: false });
      setDelivery(data);
    } catch (e) {
      toast.error("Could not fetch delivery info");
    }
  }

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pt-10">
        <div className="overline text-[#163A2F]/60 mb-4">
          <Link to="/" className="hover:text-[#C9A227]">Home</Link> · <Link to="/shop" className="hover:text-[#C9A227]">Shop</Link> · <span className="text-[#C9A227]">{product.name}</span>
        </div>
      </div>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-8 grid lg:grid-cols-12 gap-10">
        {/* GALLERY */}
        <div className="lg:col-span-7 grid grid-cols-12 gap-4">
          <div className="hidden md:flex flex-col gap-3 col-span-2">
            {product.images?.map((img, i) => (
              <button
                key={i}
                data-testid={`thumb-${i}`}
                onClick={() => setActive(i)}
                className={`aspect-square overflow-hidden border ${active === i ? "border-[#C9A227]" : "border-transparent"}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="col-span-12 md:col-span-10">
            <div className="aspect-[4/5] bg-[#163A2F]/5 overflow-hidden">
              <img
                src={product.images?.[active]}
                alt={product.name}
                data-testid="product-main-image"
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
            <div className="md:hidden mt-3 grid grid-cols-4 gap-2">
              {product.images?.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`aspect-square overflow-hidden border ${active === i ? "border-[#C9A227]" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* DETAILS */}
        <div className="lg:col-span-5">
          <div className="overline text-[#C9A227]">{product.category?.replace(/-/g, " ")}</div>
          <h1 data-testid="product-name" className="mt-3 text-3xl md:text-4xl font-light text-[#163A2F] tracking-tight leading-tight">{product.name}</h1>

          <div className="mt-4 flex items-center gap-3">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-[#C9A227] text-[#C9A227]" : "text-[#163A2F]/20"}`} />
              ))}
            </div>
            <span className="text-sm text-[#163A2F]/60">{product.rating} · {product.reviews_count} reviews</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <div data-testid="product-price" className="text-3xl font-light text-[#163A2F]">{formatINR(product.price)}</div>
            {product.original_price && (
              <>
                <div className="text-base line-through text-[#163A2F]/40 font-light">{formatINR(product.original_price)}</div>
                {discount && <div className="text-sm font-medium text-[#C9A227]">{discount}% off</div>}
              </>
            )}
          </div>
          <p className="mt-1 text-xs text-[#163A2F]/55">Inclusive of all taxes</p>

          <div className="my-7 gold-rule w-full" />

          <div>
            <div className="flex justify-between items-center mb-3">
              <div className="overline text-[#163A2F]">Size</div>
              <button className="text-xs underline text-[#163A2F]/60">Size guide</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {product.sizes?.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  data-testid={`size-${s}`}
                  className={`min-w-[3rem] px-4 py-2 text-sm border transition-colors ${
                    size === s ? "bg-[#163A2F] text-white border-[#163A2F]" : "text-[#163A2F] border-[#163A2F]/25 hover:border-[#163A2F]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {product.colors?.length > 0 && (
            <div className="mt-7">
              <div className="overline text-[#163A2F] mb-3">Colour: <span className="text-[#163A2F]/60 normal-case tracking-normal font-normal">{color}</span></div>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    data-testid={`color-${c}`}
                    className={`px-4 py-2 text-sm border transition-colors ${
                      color === c ? "bg-[#163A2F] text-white border-[#163A2F]" : "text-[#163A2F] border-[#163A2F]/25 hover:border-[#163A2F]"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-7">
            <div className="overline text-[#163A2F] mb-3">Quantity</div>
            <div className="inline-flex items-center border hairline">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2"><Minus className="w-4 h-4" /></button>
              <span data-testid="qty-display" className="px-5 py-2">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-2"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3">
            <button
              onClick={handleAdd}
              data-testid="add-to-cart-button"
              className="w-full bg-[#163A2F] text-white py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#0A1C16] transition-colors flex items-center justify-center gap-2"
            >
              <ShoppingBag className="w-4 h-4" /> Add to bag
            </button>
            <button
              onClick={handleBuyNow}
              data-testid="buy-now-button"
              className="w-full bg-[#C9A227] text-white py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#DCC267] transition-colors"
            >
              Buy now
            </button>
          </div>

          {/* Delivery checker */}
          <div className="mt-8 border hairline p-5">
            <div className="overline text-[#C9A227] mb-3">Check delivery</div>
            <div className="flex gap-2">
              <input
                value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="6-digit pincode"
                data-testid="pincode-input"
                className="flex-1 px-3 py-2 border hairline bg-transparent focus:outline-none focus:border-[#C9A227]"
              />
              <button data-testid="check-pincode" onClick={checkDelivery} className="px-5 bg-[#163A2F] text-white text-xs uppercase tracking-[0.2em]">Check</button>
            </div>
            {delivery && (
              <div className="mt-3 text-sm space-y-1">
                {delivery.couriers.map(c => (
                  <div key={c.id} className="flex justify-between text-[#163A2F]">
                    <span>{c.name}</span><span className="font-medium">{formatINR(c.rate)} · {c.eta_days}d</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4 text-center text-[#163A2F]">
            {[
              { i: Truck, t: "10% off first order" },
              { i: ShieldCheck, t: "Authentic craft" },
              { i: RotateCcw, t: "7-day exchange" },
            ].map(({ i: Ic, t }, idx) => (
              <div key={idx}>
                <Ic className="w-5 h-5 mx-auto text-[#C9A227]" strokeWidth={1.5} />
                <div className="mt-2 text-[11px] font-light">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-12 grid lg:grid-cols-2 gap-12">
        <ScrollFade>
          <div className="overline text-[#C9A227]">Description</div>
          <h2 className="mt-3 text-2xl md:text-3xl font-light text-[#163A2F]">The making of this piece</h2>
          <p className="mt-5 text-[#163A2F]/75 leading-relaxed font-light">{product.description}</p>
        </ScrollFade>
        <ScrollFade delay={0.1}>
          <div className="overline text-[#C9A227]">Specifications</div>
          <dl className="mt-3 space-y-3 text-sm">
            {product.fabric && <div className="flex justify-between border-b hairline py-3"><dt className="text-[#163A2F]/60">Fabric</dt><dd className="text-[#163A2F]">{product.fabric}</dd></div>}
            {product.care && <div className="flex justify-between border-b hairline py-3"><dt className="text-[#163A2F]/60">Care</dt><dd className="text-[#163A2F] text-right max-w-xs">{product.care}</dd></div>}
            <div className="flex justify-between border-b hairline py-3"><dt className="text-[#163A2F]/60">Origin</dt><dd className="text-[#163A2F]">Made in India</dd></div>
            <div className="flex justify-between border-b hairline py-3"><dt className="text-[#163A2F]/60">Stock</dt><dd className="text-[#163A2F]">{product.stock} units</dd></div>
          </dl>
        </ScrollFade>
      </section>

      {related.length > 0 && (
        <section className="bg-[#F8F6F0] py-20 border-t hairline">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
            <ScrollFade>
              <div className="overline text-[#C9A227]">Pairs well with</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-light text-[#163A2F]">You may also like</h2>
            </ScrollFade>
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {related.map(p => <ProductCard product={p} key={p.id} />)}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
